import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mealAPI } from '../services/api';

const MealDetail = () => {
  const { mealId } = useParams();
  const navigate = useNavigate();
  const [meals, setMeals] = useState([]);
  const [currentMeal, setCurrentMeal] = useState(null);
  const [recitations, setRecitations] = useState([]);
  const [selectedRecitation, setSelectedRecitation] = useState(null);
  const [activeTab, setActiveTab] = useState('turkish');
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioError, setAudioError] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    loadMeals();
  }, []);

  useEffect(() => {
    if (meals.length > 0 && mealId) {
      loadMealData(mealId);
    }
  }, [mealId, meals]);

  // Reset audio when selectedRecitation changes
  useEffect(() => {
    if (selectedRecitation && audioRef.current) {
      setIsPlaying(false);
      setCurrentTime(0);
      setAudioError(false);
      setAudioReady(false);
      audioRef.current.load();
    }
  }, [selectedRecitation]);

  const loadMeals = async () => {
    try {
      const response = await mealAPI.getAll();
      setMeals(response.data || []);
    } catch (error) {
      console.error('Error loading meals:', error);
    }
  };

  const loadMealData = async (id) => {
    setLoading(true);
    setAudioError(false); // Reset error state
    try {
      // Load meal info
      const mealRes = await mealAPI.getOne(id);
      setCurrentMeal(mealRes.data);
      
      // SADECE meal'in kendi audio dosyası varsa onu kullan
      if (mealRes.data.audioUrl && mealRes.data.audioUrl.trim() !== '') {
        const mealAudio = {
          id: `meal-${mealRes.data.id}`,
          audioUrl: mealRes.data.audioUrl,
          duration: mealRes.data.duration || '00:00',
          surahName: mealRes.data.name
        };
        
        setRecitations([mealAudio]);
        setSelectedRecitation(mealAudio);
      } else {
        setRecitations([]);
        setSelectedRecitation(null);
      }
      
    } catch (error) {
      console.error('Error loading meal data:', error);
      navigate('/meal');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMeal = () => {
    if (meals.length === 0) return;
    const currentIndex = meals.findIndex(m => m.id === mealId);
    if (currentIndex > 0) {
      navigate(`/meal/${meals[currentIndex - 1].id}`);
    }
  };

  const handleNextMeal = () => {
    if (meals.length === 0) return;
    const currentIndex = meals.findIndex(m => m.id === mealId);
    if (currentIndex < meals.length - 1) {
      navigate(`/meal/${meals[currentIndex + 1].id}`);
    }
  };

  const handleTrackChange = (meal) => {
    navigate(`/meal/${meal.id}`);
  };

  if (loading || !currentMeal) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F9F6F1]">
        <p className="text-[#8B7355]">Yukleniyor...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F1] pb-32 relative" 
         style={{
           backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23E8DCC8\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
           backgroundSize: '60px 60px'
         }}
    >
      {/* Main Content Container */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Navigation Section */}
        <div className="flex items-center justify-between mb-4">
          {/* Previous Meal */}
          <button
            onClick={handlePreviousMeal}
            disabled={meals.findIndex(m => m.id === mealId) === 0}
            className="flex items-center space-x-2 text-[#6B5744] hover:text-[#5C4033] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-6 w-6" />
            <div className="text-left">
              <p className="text-xs text-[#8B7355]">Önceki</p>
              <p className="text-sm font-medium">
                {meals.findIndex(m => m.id === mealId) > 0 
                  ? meals[meals.findIndex(m => m.id === mealId) - 1]?.name 
                  : ''}
              </p>
            </div>
          </button>

          {/* Next Meal */}
          <button
            onClick={handleNextMeal}
            disabled={meals.findIndex(m => m.id === mealId) === meals.length - 1}
            className="flex items-center space-x-2 text-[#6B5744] hover:text-[#5C4033] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <div className="text-right">
              <p className="text-xs text-[#8B7355]">Sonraki</p>
              <p className="text-sm font-medium">
                {meals.findIndex(m => m.id === mealId) < meals.length - 1 
                  ? meals[meals.findIndex(m => m.id === mealId) + 1]?.name 
                  : ''}
              </p>
            </div>
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Title Section */}
        <div className="text-center mb-4">
          {/* Arabic Title */}
          <div className="mb-1">
            <p className="text-4xl text-[#5C4033]" style={{ fontFamily: '"Traditional Arabic", serif' }}>
              {currentMeal.nameArabic}
            </p>
          </div>
          
          {/* Turkish Title */}
          <h1 className="text-3xl font-bold text-[#5C4033] mb-1">{currentMeal.name}</h1>
        </div>

        {/* Audio Player - Always show if we have recitations */}
        {recitations.length > 0 && selectedRecitation && (
          <div className="mb-6">
            {/* Hidden audio element */}
            <audio
              ref={audioRef}
              src={selectedRecitation.audioUrl ? `${process.env.REACT_APP_BACKEND_URL}${selectedRecitation.audioUrl.replace('/uploads/', '/api/files/')}` : ''}
              onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
              onLoadedMetadata={(e) => {
                setDuration(e.target.duration);
                setAudioReady(true);
              }}
              onCanPlay={() => setAudioReady(true)}
              onEnded={() => setIsPlaying(false)}
              onError={(e) => {
                console.warn('Audio file unavailable for this recitation');
                setIsPlaying(false);
                setAudioError(true);
                setAudioReady(false);
              }}
            />
            
            {/* Audio player card - Simple and clean */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-[#E8DCC8]">
              {/* Player Controls */}
              <div className="flex items-center space-x-4">
                {/* Play/Pause Button */}
                <button 
                  onClick={async () => {
                    if (!audioRef.current) return;
                    
                    try {
                      if (isPlaying) {
                        audioRef.current.pause();
                        setIsPlaying(false);
                      } else {
                        // Try to play with promise handling
                        const playPromise = audioRef.current.play();
                        if (playPromise !== undefined) {
                          await playPromise;
                          setIsPlaying(true);
                        }
                      }
                    } catch (error) {
                      console.warn('Audio playback failed:', error.message);
                      setIsPlaying(false);
                      setAudioError(true);
                    }
                  }}
                  disabled={!audioReady}
                  className="flex-shrink-0 w-12 h-12 rounded-full bg-[#8B7355] hover:bg-[#6B5744] text-white flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPlaying ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  )}
                </button>

                {/* Progress Bar */}
                <div className="flex-1">
                  <div 
                    className="h-2 bg-[#E8DCC8] rounded-full overflow-hidden cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = x / rect.width;
                      const newTime = percentage * duration;
                      if (audioRef.current) {
                        audioRef.current.currentTime = newTime;
                      }
                    }}
                  >
                    <div 
                      className="h-full bg-[#8B7355] rounded-full transition-all" 
                      style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-xs text-[#8B7355]">
                      {Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, '0')}
                    </span>
                    <span className="text-xs text-[#8B7355]">
                      {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs for Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 bg-transparent gap-2 h-auto p-0">
            <TabsTrigger 
              value="turkish"
              className="data-[state=active]:bg-[#8B7355] data-[state=active]:text-white bg-white/80 text-[#6B5744] border border-[#E8DCC8] rounded-lg py-3 hover:bg-[#F5EFE7] transition-all"
            >
              Türkçe
            </TabsTrigger>
            <TabsTrigger 
              value="both"
              className="data-[state=active]:bg-[#8B7355] data-[state=active]:text-white bg-white/80 text-[#6B5744] border border-[#E8DCC8] rounded-lg py-3 hover:bg-[#F5EFE7] transition-all"
            >
              Arapça-Türkçe
            </TabsTrigger>
            <TabsTrigger 
              value="arabic"
              className="data-[state=active]:bg-[#8B7355] data-[state=active]:text-white bg-white/80 text-[#6B5744] border border-[#E8DCC8] rounded-lg py-3 hover:bg-[#F5EFE7] transition-all"
            >
              Arapça
            </TabsTrigger>
          </TabsList>

          {/* Turkish Content */}
          <TabsContent value="turkish">
            <Card className="bg-white/80 backdrop-blur-sm border border-[#E8DCC8] p-8 shadow-lg rounded-2xl">
              <div className="prose prose-lg max-w-none">
                <p className="text-[#5C4033] text-lg leading-relaxed whitespace-pre-wrap">
                  {currentMeal.contentTurkish || 'Türkçe içerik henüz eklenmemiş.'}
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* Both Turkish and Arabic Content */}
          <TabsContent value="both">
            <div className="space-y-6">
              {/* Arabic */}
              <Card className="bg-white/80 backdrop-blur-sm border border-[#E8DCC8] p-8 shadow-lg rounded-2xl">
                <h3 className="text-sm font-semibold text-[#8B7355] mb-4">عربي</h3>
                <div className="text-right" style={{ fontFamily: '"Scheherazade New", serif' }}>
                  <p className="text-[#5C4033] text-2xl leading-loose whitespace-pre-wrap">
                    {currentMeal.contentArabic || 'المحتوى العربي لم يتم إضافته بعد.'}
                  </p>
                </div>
              </Card>
              
              {/* Turkish */}
              <Card className="bg-white/80 backdrop-blur-sm border border-[#E8DCC8] p-8 shadow-lg rounded-2xl">
                <h3 className="text-sm font-semibold text-[#8B7355] mb-4">Türkçe</h3>
                <div>
                  <p className="text-[#5C4033] text-lg leading-relaxed whitespace-pre-wrap">
                    {currentMeal.contentTurkish || 'Türkçe içerik henüz eklenmemiş.'}
                  </p>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Arabic Content */}
          <TabsContent value="arabic">
            <Card className="bg-white/80 backdrop-blur-sm border border-[#E8DCC8] p-8 shadow-lg rounded-2xl">
              <div className="prose prose-lg max-w-none text-right" style={{ fontFamily: '"Scheherazade New", serif' }}>
                <p className="text-[#5C4033] text-2xl leading-loose whitespace-pre-wrap">
                  {currentMeal.contentArabic || 'المحتوى العربي لم يتم إضافته بعد.'}
                </p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

    </div>
  );
};

export default MealDetail;